import {
  AttributesApiGetAttributesRequest,
  CategoryApiGetCategoriesRequest,
  CategoryApiGetNestedCategoriesRequest,
  ChatEntriesApiGetChatEntriesRequest,
  DelayIndexApiGetDelayIndexPerCountryRequest,
  DelayIndexApiGetDelayIndexPerDiseaseRequest,
  DelayIndexApiGetDelayIndexRequest,
  DiseasesApiGetDiseasesRequest,
  DiseasesApiGetDiseasesTrialsCountRequest,
  HistoryType,
  LocationApiGetLocationsRequest,
  QuestionsApiGetQuestionCategoriesRequest,
  QuestionsApiGetQuestionCriteriaRequest,
  QuestionsApiGetQuestionsRequest,
  QuestionsApiGetSuggestedQuestionsRequest,
  QuestionType,
  Role,
  SurveyManagementApiGetDescriptionTranslationsRequest,
  SurveyManagementApiGetSurveyDetailsRequest,
  SurveyManagementApiGetSurveyQuestionsRequest,
  SurveyManagementApiGetSurveysRequest,
  SurveyManagementApiGetTitleTranslationsRequest,
  TrialManagementApiGetLeadSponsorsRequest,
  TrialManagementApiGetLocationsRequest,
  TrialManagementApiGetMatchesRequest,
  TrialManagementApiGetPublicTrialsRequest,
  TrialManagementApiGetTeamMandatesRequest,
  TrialManagementApiSearchRequest,
  UserSurveysApiGetMySurveyDetailRequest,
  UserSurveysApiGetMySurveysRequest,
  UserTrialsApiGetMyTrialLocationsRequest,
  UserTrialStatus,
} from '@data/sdk/match';
import {
  DefaultQueryParams,
  DiseaseSearchParams,
  FunnelActivityQueryParams,
  ICDCodesSearchParams,
  PermissionsQueryParams,
  TrialVolunteerAnswersParams,
  UserTrialsQueryParams,
} from '../utils/types';

export const completionPercentageKeys = {
  all: () => ['completionPercentage'] as const,
  lists: () => [...completionPercentageKeys.all(), 'list'] as const,
};

export const attributesKeys = {
  all: () => ['attributes'] as const,
  lists: () => [...attributesKeys.all(), 'list'] as const,
  list: (filters: AttributesApiGetAttributesRequest) =>
    [...attributesKeys.lists(), { filters }] as const,
};

export const chatItemsKeys = {
  all: () => ['chatItems'] as const,
  allByHistoryType: (type: HistoryType) => [...chatItemsKeys.all(), type] as const,
  lists: (type: HistoryType) => [...chatItemsKeys.allByHistoryType(type), 'list'] as const,
  list: (type: HistoryType, filters: ChatEntriesApiGetChatEntriesRequest) =>
    [...chatItemsKeys.lists(type), { filters }] as const,
  details: () => [...chatItemsKeys.all(), 'detail'] as const,
  detail: (id: string) => [...chatItemsKeys.details(), id] as const,
};

export const diseasesKeys = {
  all: () => ['diseases'] as const,
  lists: () => [...diseasesKeys.all(), 'list'] as const,
  list: (filters: DiseasesApiGetDiseasesRequest) => [...diseasesKeys.lists(), { filters }] as const,
  trialsCount: (filters: DiseasesApiGetDiseasesTrialsCountRequest) =>
    [...diseasesKeys.lists(), 'trialsCount', { filters }] as const,
};

export const userTrialsKeys = {
  all: () => ['userTrials'] as const,
  allPerStatus: (status: UserTrialStatus) => [...userTrialsKeys.all(), status] as const,
  lists: () => [...userTrialsKeys.all(), 'list'] as const,
  list: (filters: UserTrialsQueryParams) => [...userTrialsKeys.lists(), { filters }] as const,
  details: () => [...userTrialsKeys.all(), 'detail'] as const,
  detail: (id: string) => [...userTrialsKeys.details(), id] as const,
};

export const trialsKeys = {
  all: () => ['trials'] as const,
  allForTeam: (teamId?: string | null) =>
    [...trialsKeys.all(), ...(teamId ? [teamId] : [])] as const,
  list: (teamId?: string | null, filters?: TrialManagementApiSearchRequest['getTrialsBody']) =>
    [...trialsKeys.allForTeam(teamId), { filters }] as const,
  details: () => [...trialsKeys.all(), 'detail'] as const,
  detail: (id: string) => [...trialsKeys.details(), id] as const,
};

export const publicTrialsKeys = {
  all: () => ['publicTrials'] as const,
  lists: () => [...publicTrialsKeys.all(), 'list'] as const,
  list: (filters?: TrialManagementApiGetPublicTrialsRequest) =>
    [...publicTrialsKeys.lists(), { filters }] as const,
};

export const trialCriteriaKeys = {
  all: () => ['trialCriteria'] as const,
  allForTrial: (id: string) => [...trialCriteriaKeys.all(), id] as const,
  lists: (id: string) => [...trialCriteriaKeys.allForTrial(id), 'list'] as const,
  list: (id: string, filters: DefaultQueryParams) =>
    [...trialCriteriaKeys.lists(id), { filters }] as const,
};

export const criteriaActivityKeys = {
  all: () => ['criteriaActivity'] as const,
  allForCriteria: (id: string) => [...criteriaActivityKeys.all(), id] as const,
  lists: (id: string) => [...criteriaActivityKeys.allForCriteria(id), 'list'] as const,
  list: (id: string, filters: DefaultQueryParams) =>
    [...criteriaActivityKeys.lists(id), { filters }] as const,
};

export const tokenDataKeys = {
  all: () => ['tokenData'] as const,
  lists: () => [...tokenDataKeys.all(), 'list'] as const,
};

export const configurationsKeys = {
  all: () => ['configurations'] as const,
  maintenance: () => [...configurationsKeys.all(), 'maintenance'] as const,
  questions: () => [...configurationsKeys.all(), 'questions'] as const,
  tags: () => [...configurationsKeys.all(), 'tags'] as const,
};

export const criteriaQuestionsKeys = {
  all: () => ['criteriaQuestions'] as const,
  allForType: (types?: QuestionType[]) => [...criteriaQuestionsKeys.all(), types] as const,
  lists: (types?: QuestionType[]) => [...criteriaQuestionsKeys.allForType(types), 'list'] as const,
  list: (filters: QuestionsApiGetQuestionsRequest) =>
    [...criteriaQuestionsKeys.lists(filters.getQuestionsBody.questionTypes), { filters }] as const,
  details: () => [...criteriaQuestionsKeys.all(), 'detail'] as const,
  detail: (questionId: string) => [...criteriaQuestionsKeys.details(), questionId] as const,
};

export const trialActivityKeys = {
  all: () => ['trialActivity'] as const,
  lists: () => [...trialActivityKeys.all(), 'list'] as const,
  list: (id: string) => [...trialActivityKeys.lists(), id] as const,
};

export const userTrialContactsKeys = {
  all: () => ['userTrialContacts'] as const,
  lists: () => [...userTrialContactsKeys.all(), 'list'] as const,
  list: (id: string) => [...userTrialContactsKeys.lists(), id] as const,
  currentFlow: () => [...userTrialContactsKeys.all(), 'currentFlow'] as const,
};

export const trialContactsKeys = {
  all: () => ['trialContacts'] as const,
  lists: () => [...trialContactsKeys.all(), 'list'] as const,
  list: (trialId: string) => [...trialContactsKeys.lists(), trialId] as const,
  details: () => [...trialContactsKeys.all(), 'detail'] as const,
  detail: (contactId: string) => [...trialContactsKeys.details(), contactId] as const,
  contactOptions: (trialId: string) =>
    [...trialContactsKeys.all(), 'contactOptions', trialId] as const,
};

export const trialLocationsKeys = {
  all: () => ['trialLocations'] as const,
  lists: () => [...trialLocationsKeys.all(), 'list'] as const,
  list: (filters: TrialManagementApiGetLocationsRequest) =>
    [...trialLocationsKeys.lists(), { filters }] as const,
  details: () => [...trialLocationsKeys.all(), 'detail'] as const,
  detail: (locationId: string) => [...trialLocationsKeys.details(), locationId] as const,
};

export const questionTranslationsKeys = {
  all: () => ['questionTranslations'] as const,
  lists: () => [...questionTranslationsKeys.all(), 'list'] as const,
  list: (id: string) => [...questionTranslationsKeys.lists(), id] as const,
};

export const questionDependenciesKeys = {
  all: () => ['questionDependencies'] as const,
  lists: () => [...questionDependenciesKeys.all(), 'list'] as const,
  list: (id: string) => [...questionDependenciesKeys.lists(), id] as const,
  details: () => [...questionDependenciesKeys.all(), 'detail'] as const,
  detail: (dependencyId: string) => [...questionDependenciesKeys.details(), dependencyId] as const,
};

export const similarQuestionsKeys = {
  all: () => ['similarQuestions'] as const,
  lists: () => [...similarQuestionsKeys.all(), 'list'] as const,
  list: (filters: DefaultQueryParams) => [...similarQuestionsKeys.lists(), { filters }] as const,
  details: () => [...similarQuestionsKeys.all(), 'detail'] as const,
  detail: (id: string) => [...similarQuestionsKeys.details(), id] as const,
};

export const suggestedQuestionsKeys = {
  all: () => ['suggestedQuestions'] as const,
  lists: () => [...suggestedQuestionsKeys.all(), 'list'] as const,
  list: (filters: QuestionsApiGetSuggestedQuestionsRequest) =>
    [...suggestedQuestionsKeys.lists(), { filters }] as const,
};

export const trialTitleTranslationKeys = {
  all: () => ['trialTitleTranslation'] as const,
  lists: () => [...trialTitleTranslationKeys.all(), 'list'] as const,
  list: (id: string) => [...trialTitleTranslationKeys.lists(), id] as const,
};

export const trialTeamsKeys = {
  all: () => ['trialTeams'] as const,
  lists: () => [...trialTitleTranslationKeys.all(), 'list'] as const,
  list: (id: string) => [...trialTitleTranslationKeys.lists(), id] as const,
};

export const trialSummaryTranslationKeys = {
  all: () => ['trialSummaryTranslation'] as const,
  lists: () => [...trialSummaryTranslationKeys.all(), 'list'] as const,
  list: (id: string) => [...trialSummaryTranslationKeys.lists(), id] as const,
};

export const icdCodesKeys = {
  all: () => ['icdCodes'] as const,
  lists: () => [...icdCodesKeys.all(), 'list'] as const,
  list: (filters: ICDCodesSearchParams) => [...icdCodesKeys.lists(), { filters }] as const,
};

export const volunteerOverviewKeys = {
  all: () => ['volunteerOverview'] as const,
  lists: () => [...volunteerOverviewKeys.all(), 'list'] as const,
  list: (id: string) => [...volunteerOverviewKeys.lists(), id] as const,
  listByCountry: (id: string, locationId?: string) =>
    [...volunteerOverviewKeys.list(id), locationId] as const,
};

export const volunteerAnswersKeys = {
  all: () => ['volunteerAnswers'] as const,
  lists: () => [...volunteerAnswersKeys.all(), 'list'] as const,
  allForTrial: (id: string) => [...volunteerAnswersKeys.all(), id] as const,
  list: (id: string, filters: TrialVolunteerAnswersParams) =>
    [...volunteerAnswersKeys.allForTrial(id), { filters }] as const,
  details: () => [...volunteerAnswersKeys.all(), 'detail'] as const,
  detail: (id: string) => [...volunteerAnswersKeys.details(), id] as const,
};

export const matchedVolunteersKeys = {
  all: () => ['matchedVolunteers'] as const,
  allForTrial: (id: string) => [...matchedVolunteersKeys.all(), id] as const,
  lists: (id: string) => [...matchedVolunteersKeys.allForTrial(id), 'list'] as const,
  list: ({ id, ...params }: TrialManagementApiGetMatchesRequest) =>
    [...matchedVolunteersKeys.lists(id), { params }] as const,
};

export const volunteerContactsKeys = {
  all: () => ['volunteerContacts'] as const,
  lists: () => [...volunteerContactsKeys.all(), 'list'] as const,
  list: (id: string) => [...volunteerContactsKeys.lists(), id] as const,
};

export const funnelActivityKeys = {
  all: () => ['funnelActivity'] as const,
  lists: () => [...funnelActivityKeys.all(), 'list'] as const,
  list: (filters: FunnelActivityQueryParams) =>
    [...funnelActivityKeys.lists(), { filters }] as const,
  details: () => [...funnelActivityKeys.all(), 'detail'] as const,
  detail: (id: string) => [...funnelActivityKeys.details(), id] as const,
};

export const funnelActivityUsersKeys = {
  all: () => ['funnelActivityUsers'] as const,
  lists: () => [...funnelActivityUsersKeys.all(), 'list'] as const,
  list: (params: DefaultQueryParams) => [...funnelActivityUsersKeys.lists(), { params }] as const,
};

export const trialPermissionsKeys = {
  all: () => ['trialPermissions'] as const,
  lists: () => [...trialPermissionsKeys.all(), 'list'] as const,
  list: (trialId: string) => [...trialPermissionsKeys.lists(), trialId] as const,
  details: () => [...trialPermissionsKeys.all(), 'detail'] as const,
  detail: (id: string) => [...trialPermissionsKeys.details(), id] as const,
};

export const trialAccessRequestsKeys = {
  all: () => ['trialAccessRequests'] as const,
  lists: () => [...trialAccessRequestsKeys.all(), 'list'] as const,
  list: (trialId: string) => [...trialAccessRequestsKeys.lists(), trialId] as const,
  details: () => [...trialAccessRequestsKeys.all(), 'detail'] as const,
  detail: (id: string) => [...trialAccessRequestsKeys.details(), id] as const,
};

export const userTrialPermissionsKeys = {
  all: () => ['userTrialPermissions'] as const,
  lists: () => [...userTrialPermissionsKeys.all(), 'list'] as const,
  list: (id: string) => [...userTrialPermissionsKeys.lists(), id] as const,
};

export const trialInvitesKeys = {
  all: () => ['trialInvites'] as const,
  lists: () => [...trialInvitesKeys.all(), 'list'] as const,
  list: (trialId: string) => [...trialInvitesKeys.lists(), trialId] as const,
  details: () => [...trialInvitesKeys.all(), 'detail'] as const,
  detail: (id: string) => [...trialInvitesKeys.details(), id] as const,
};

export const volunteerTrialOverviewKeys = {
  all: () => ['volunteerTrialOverview'] as const,
  lists: () => [...volunteerTrialOverviewKeys.all(), 'list'] as const,
};

export const hospitalsKeys = {
  all: () => ['hospitals'] as const,
  lists: () => [...hospitalsKeys.all(), 'list'] as const,
  list: (filters: DiseaseSearchParams) => [...hospitalsKeys.lists(), { filters }] as const,
};

export const subscriptionPreferencesKeys = {
  all: () => ['subscriptionPreferences'] as const,
  lists: () => [...subscriptionPreferencesKeys.all(), 'list'] as const,
  list: (id: string) => [...subscriptionPreferencesKeys.lists(), id] as const,
};

export const notificationsKeys = {
  all: () => ['notifications'] as const,
  lists: () => [...notificationsKeys.all(), 'list'] as const,
  list: () => [...notificationsKeys.lists()] as const,
};

export const unreadNotificationsKeys = {
  all: () => ['unreadNotifications'] as const,
};

export const icdCodeTranslationsKeys = {
  all: () => ['icdCodeTranslations'] as const,
  lists: () => [...icdCodeTranslationsKeys.all(), 'list'] as const,
  list: (id: string) => [...icdCodeTranslationsKeys.lists(), id] as const,
};

export const icdCodeNamesTranslationsKeys = {
  all: () => ['icdCodeNamesTranslations'] as const,
  lists: () => [...icdCodeNamesTranslationsKeys.all(), 'list'] as const,
  list: (id: string) => [...icdCodeNamesTranslationsKeys.lists(), id] as const,
  detail: (id: string, filters: DiseaseSearchParams) =>
    [...icdCodeNamesTranslationsKeys.list(id), { filters }] as const,
};

export const hospitalCentersKeys = {
  all: () => ['hospitalCenters'] as const,
  lists: () => [...hospitalCentersKeys.all(), 'list'] as const,
  list: (filters: ICDCodesSearchParams) => [...hospitalCentersKeys.lists(), { filters }] as const,
};

export const hospitalCenterTranslationsKeys = {
  all: () => ['hospitalCenterTranslations'] as const,
  lists: () => [...hospitalCenterTranslationsKeys.all(), 'list'] as const,
  list: (id: string) => [...hospitalCenterTranslationsKeys.lists(), id] as const,
};

export const hospitalCenterNamesKeys = {
  all: () => ['hospitalCenterNames'] as const,
  allForHospital: (id: string) => [...hospitalCenterNamesKeys.all(), id] as const,
  lists: (id: string) => [...hospitalCenterNamesKeys.allForHospital(id), 'list'] as const,
  list: (id: string, filters: DiseaseSearchParams) =>
    [...hospitalCenterNamesKeys.lists(id), { filters }] as const,
  details: () => [...hospitalCenterNamesKeys.all(), 'detail'] as const,
  detail: (id: string) => [...hospitalCenterNamesKeys.details(), id] as const,
};

export const hospitalCenterDetailsKeys = {
  all: () => ['hospitalCenterDetails'] as const,
  lists: () => [...hospitalCenterDetailsKeys.all(), 'list'] as const,
  list: (id: string) => [...hospitalCenterDetailsKeys.lists(), id] as const,
};

export const leadSponsorKeys = {
  all: () => ['leadSponsor'] as const,
  lists: () => [...leadSponsorKeys.all(), 'list'] as const,
  list: (filters: TrialManagementApiGetLeadSponsorsRequest) =>
    [...leadSponsorKeys.lists(), { filters }] as const,
};

export const userTrialLocationsKeys = {
  all: () => ['userTrialLocations'] as const,
  lists: () => [...userTrialLocationsKeys.all(), 'list'] as const,
  list: (filters: UserTrialsApiGetMyTrialLocationsRequest) =>
    [...userTrialLocationsKeys.lists(), { filters }] as const,
  details: () => [...userTrialLocationsKeys.all(), 'detail'] as const,
  detail: (id: string) => [...userTrialLocationsKeys.details(), id] as const,
};

export const invitesKeys = {
  all: () => ['invites'] as const,
  allForRole: (role?: Role) => [...invitesKeys.all(), role] as const,
  lists: (role: Role) => [...invitesKeys.allForRole(role), 'list'] as const,
  list: (role: Role, params: PermissionsQueryParams) =>
    [...invitesKeys.lists(role), { params }] as const,
};

export const publicTrialLocationsKeys = {
  all: (id: string) => ['publicTrialLocations', id] as const,
  lists: (id: string) => [...publicTrialLocationsKeys.all(id), 'list'] as const,
  list: (id: string, filters: DefaultQueryParams) =>
    [...publicTrialLocationsKeys.lists(id), { filters }] as const,
};

export const locationsKeys = {
  all: () => ['locations'] as const,
  lists: () => [...locationsKeys.all(), 'list'] as const,
  list: (filters: LocationApiGetLocationsRequest) =>
    [...locationsKeys.lists(), { filters }] as const,
};

export const teamMembersKeys = {
  all: () => ['teamMembers'] as const,
  lists: () => [...teamMembersKeys.all(), 'list'] as const,
  list: (teamId: string) => [...teamMembersKeys.lists(), teamId] as const,
  listCount: (teamId: string) => [...teamMembersKeys.lists(), 'count', teamId] as const,
};

export const teamInvitesKeys = {
  all: () => ['teamInvites'] as const,
  lists: () => [...teamInvitesKeys.all(), 'list'] as const,
  list: (teamId: string) => [...teamInvitesKeys.lists(), teamId] as const,
};

export const teamsKeys = {
  all: () => ['teams'] as const,
  lists: () => [...teamsKeys.all(), 'list'] as const,
  list: (teamId: string) => [...teamsKeys.lists(), teamId] as const,
  details: () => [...teamsKeys.all(), 'detail'] as const,
  detail: (id: string) => [...teamsKeys.details(), id] as const,
};

export const metadataKeys = {
  all: () => ['metadata'] as const,
};

export const questionOptionsKeys = {
  all: () => ['questionOptions'] as const,
  lists: () => [...questionOptionsKeys.all(), 'list'] as const,
  list: (id: string) => [...questionOptionsKeys.lists(), id] as const,
  details: () => [...questionOptionsKeys.all(), 'detail'] as const,
  detail: (optionId: string) => [...questionOptionsKeys.details(), optionId] as const,
};

export const billingKeys = {
  all: () => ['billing'] as const,
  checkoutSession: (teamId: string) => [...billingKeys.all(), 'checkoutSession', teamId],
  customerPortalURL: (teamId: string) => [...billingKeys.all(), 'customerPortalURL', teamId],
};

export const teamMandatesKeys = {
  all: () => ['teamMandates'] as const,
  lists: () => [...teamMandatesKeys.all(), 'list'] as const,
  list: (filters: TrialManagementApiGetTeamMandatesRequest) =>
    [...teamMandatesKeys.lists(), { filters }] as const,
};

export const categoriesKeys = {
  all: () => ['categories'] as const,
  lists: () => [...categoriesKeys.all(), 'list'] as const,
  list: (filters: CategoryApiGetCategoriesRequest) =>
    [...categoriesKeys.lists(), { filters }] as const,
  questionList: (params: QuestionsApiGetQuestionCategoriesRequest) =>
    [...categoriesKeys.all(), 'questionList', params] as const,
  question: (id: string) => [...categoriesKeys.all(), 'question', id] as const,
  categories: (filters: CategoryApiGetCategoriesRequest) =>
    [...categoriesKeys.all(), 'categories', { filters }] as const,
  trials: (id: string, filters: CategoryApiGetCategoriesRequest) =>
    [...categoriesKeys.question(id), 'trials', { filters }] as const,
  nested: (filters: CategoryApiGetNestedCategoriesRequest) =>
    [...categoriesKeys.all(), 'nested', { filters }] as const,
};

export const questionCriteriaKeys = {
  all: () => ['questionCriteria'] as const,
  lists: () => [...questionCriteriaKeys.all(), 'list'] as const,
  list: ({ id, ...params }: QuestionsApiGetQuestionCriteriaRequest) =>
    [...questionCriteriaKeys.lists(), { id, ...params }] as const,
};

export const usersKeys = {
  all: () => ['users'] as const,
  admins: () => [...usersKeys.all(), 'admins'] as const,
};

export const teamTrialsKeys = {
  all: () => ['teamTrials'] as const,
  list: (teamId: string) => [...teamTrialsKeys.all(), teamId] as const,
};

export const delayIndexKeys = {
  all: () => ['delayIndex'] as const,
  details: (params: DelayIndexApiGetDelayIndexRequest) =>
    [...delayIndexKeys.all(), { params }] as const,
  detailsPerDisease: (params: DelayIndexApiGetDelayIndexPerDiseaseRequest) =>
    [...delayIndexKeys.all(), 'detailsPerDisease', { params }] as const,
  detailsPerCountry: (params: DelayIndexApiGetDelayIndexPerCountryRequest) =>
    [...delayIndexKeys.all(), 'detailsPerCountry', { params }] as const,
};

export const surveysKeys = {
  all: () => ['surveys'] as const,
  list: (params: SurveyManagementApiGetSurveysRequest) =>
    [...surveysKeys.all(), { params }] as const,
  details: (params: SurveyManagementApiGetSurveyDetailsRequest) =>
    [...surveysKeys.all(), 'details', { params }] as const,
  questions: (params: SurveyManagementApiGetSurveyQuestionsRequest) =>
    [...surveysKeys.all(), 'questions', { params }] as const,
  titleTranslations: (params: SurveyManagementApiGetTitleTranslationsRequest) =>
    [...surveysKeys.all(), 'titleTranslations', { params }] as const,
  descriptionTranslations: (params: SurveyManagementApiGetDescriptionTranslationsRequest) =>
    [...surveysKeys.all(), 'descriptionTranslations', { params }] as const,
};

export const userSurveysKeys = {
  all: () => ['userSurveys'] as const,
  lists: () => [...userSurveysKeys.all(), 'list'] as const,
  list: (params: UserSurveysApiGetMySurveysRequest) =>
    [...userSurveysKeys.lists(), { params }] as const,
  overview: () => [...userSurveysKeys.all(), 'overview'] as const,
  details: () => [...userSurveysKeys.all(), 'details'] as const,
  detail: (params: UserSurveysApiGetMySurveyDetailRequest) =>
    [...userSurveysKeys.details(), { params }] as const,
};
